/**
 * Панель «С телефона» (PROJECT_NOTES §37): одноразовая ссылка в QR-коде, компьютер ждет, пока телефон сохранит точку.
 * Ссылка живет 10 минут в памяти бэкенда; новая ссылка отменяет прежнюю, поэтому «Новый код» безопасен.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import { CheckCircleOutline, ContentCopy } from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import { notify } from '../notifications';
import { apiErrorMessage } from '../admin/reports/edit/editApi';
import { copyText } from '../admin/mathReports/changes/copyText';
import { createHandoff, fetchHandoffStatus, HandoffStatus, phoneLink, SavedPoint } from './geoApi';
import { formatCountdown } from './geoMath';
import { alertSx, BORDER, cardSx, hintSx, MUTED, outlinedButtonSx, primaryButtonSx, softCardSx, stepNumberSx, TEXT } from './geoStyles';

interface PhoneHandoffPanelProps {
  onSaved: (geo: SavedPoint, message: string) => void;
  onBack: () => void;
}

interface Handoff {
  token: string;
  deadline: number;
}

const POLL_MS = 2000;
const QR_SIZE = 176;

const STEPS = [
  'Откройте камеру телефона и наведите ее на QR-код',
  'Откройте ссылку и разрешите доступ к местоположению',
  'Поставьте пин на вход в аптеку и нажмите «Сохранить»: профиль здесь обновится сам'
];

const STATUS_TEXT: Record<HandoffStatus, string> = {
  pending: 'Ждем, когда откроете ссылку на телефоне',
  opened: 'Ссылка открыта на телефоне, ждем сохранения',
  completed: 'Точка сохранена с телефона',
  expired: 'Срок кода истек'
};

const PhoneHandoffPanel: React.FC<PhoneHandoffPanelProps> = ({ onSaved, onBack }) => {
  const [handoff, setHandoff] = useState<Handoff | null>(null);
  const [createError, setCreateError] = useState('');
  const [status, setStatus] = useState<HandoffStatus>('pending');
  const [now, setNow] = useState(() => Date.now());
  const issueSeq = useRef(0);
  const onSavedRef = useRef(onSaved);
  onSavedRef.current = onSaved;

  // Ответ на устаревший запрос (нажали «Новый код» еще раз или закрыли панель) не показывается
  const issue = useCallback(() => {
    const seq = ++issueSeq.current;
    setHandoff(null);
    setCreateError('');
    setStatus('pending');
    createHandoff()
      .then(next => { if (seq === issueSeq.current) setHandoff(next); })
      .catch(err => { if (seq === issueSeq.current) setCreateError(apiErrorMessage(err, 'Не удалось создать код для телефона')); });
  }, []);

  useEffect(() => {
    issue();
    return () => { issueSeq.current += 1; };
  }, [issue]);

  // Опрос цепочкой таймеров: следующий запрос - только после ответа на предыдущий
  useEffect(() => {
    if (!handoff) return undefined;
    let cancelled = false;
    let timer = 0;
    const poll = () => {
      fetchHandoffStatus(handoff.token)
        .then(state => {
          if (cancelled) return;
          setStatus(state.status);
          if (state.status === 'completed' && state.geolocation) {
            const geo = state.geolocation;
            onSavedRef.current(geo, geo.address ? 'Местоположение сохранено с телефона' : 'Местоположение сохранено с телефона, но адрес по нему определить не удалось');
            return;
          }
          if (state.status !== 'expired') timer = window.setTimeout(poll, POLL_MS);
        })
        // Сеть моргнула или бэкенд перезапускается - продолжаем ждать, пока не выйдет срок
        .catch(() => { if (!cancelled) timer = window.setTimeout(poll, POLL_MS); });
    };
    timer = window.setTimeout(poll, POLL_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [handoff]);

  useEffect(() => {
    if (!handoff) return undefined;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [handoff]);

  const link = handoff ? phoneLink(handoff.token) : '';
  const msLeft = handoff ? handoff.deadline - now : 0;
  const expired = status === 'expired' || (handoff !== null && status !== 'completed' && msLeft <= 0);

  const copyLink = async () => {
    if (await copyText(link)) notify.success('Ссылка скопирована');
    else notify.error('Не удалось скопировать ссылку');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2.5, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
        <Box sx={{
          ...cardSx, p: 1.5, alignSelf: 'center', width: QR_SIZE + 26, height: QR_SIZE + 26, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative'
        }}>
          {handoff ? (
            <Box sx={{ lineHeight: 0, opacity: expired ? 0.15 : 1, transition: 'opacity 0.2s ease' }}>
              <QRCodeSVG value={link} size={QR_SIZE} level="M" />
            </Box>
          ) : !createError && <CircularProgress size={28} />}
          {status === 'completed' && (
            <CheckCircleOutline sx={{ position: 'absolute', fontSize: 72, color: '#16a34a', bgcolor: '#fff', borderRadius: '50%' }} />
          )}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, minWidth: 0, flex: 1 }}>
          {STEPS.map((text, index) => (
            <Box key={text} sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
              <Box sx={stepNumberSx}>{index + 1}</Box>
              <Typography sx={{ color: TEXT, fontSize: '0.9rem', lineHeight: 1.5 }}>{text}</Typography>
            </Box>
          ))}
          <Typography sx={hintSx}>Встаньте у входа в аптеку: на улице телефон определяет место точнее всего.</Typography>
        </Box>
      </Box>

      {createError ? (
        <Alert severity="error" sx={alertSx}>{createError}</Alert>
      ) : handoff && (
        <Box sx={{ ...softCardSx, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {!expired && status !== 'completed' && <CircularProgress size={14} thickness={5} />}
              <Typography sx={{ fontWeight: 600, color: expired ? '#b45309' : TEXT, fontSize: '0.9rem' }}>
                {expired ? STATUS_TEXT.expired : STATUS_TEXT[status]}
              </Typography>
            </Box>
            {!expired && status !== 'completed' && (
              <Typography sx={{ color: MUTED, fontSize: 13 }}>Код действует еще {formatCountdown(msLeft)}</Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
            <Typography sx={{ color: MUTED, fontSize: 12, fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {link}
            </Typography>
            <Tooltip title="Скопировать ссылку">
              <IconButton size="small" aria-label="Скопировать ссылку" onClick={copyLink} sx={{ border: `1px solid ${BORDER}`, bgcolor: '#fff', p: 0.5, flexShrink: 0 }}>
                <ContentCopy sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}

      {link.indexOf('http:') === 0 && (
        <Alert severity="warning" sx={alertSx}>
          Ссылка открывается без защищенного соединения (https): телефон не даст координаты, и пин придется ставить на карте вручную.
          Телефон должен быть в той же сети, что и этот компьютер.
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button variant="outlined" onClick={onBack} sx={{ ...outlinedButtonSx, mr: 'auto' }}>Назад</Button>
        {(expired || createError) && <Button variant="contained" onClick={issue} sx={primaryButtonSx}>Новый код</Button>}
      </Box>
    </Box>
  );
};

export default PhoneHandoffPanel;
